import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchSchedulingWindows } from 'api/scheduling_windows/endpoints/searchSchedulingWindows';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';
import { withPathsOnly } from './schedulingWindowPath';

// The minimal identifying shape this control deals in. The backend
// validates the wire format as `zone_name|window_name`, so those are
// the two fields the editable entity needs.
export interface SchedulingWindowReference {
  readonly zone_name: string;
  readonly name: string;
}

interface Props {
  readonly value: SchedulingWindowReference | null;
  readonly onChange: (value: SchedulingWindowReference | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // every window the user can see.
  readonly zoneName?: string;
}

// `${zone_name}|${name}` matches the wire format and is unique by
// (zone, name) — adequate for both React keys and selection match.
const getKey = (w: SchedulingWindowReference) => `${w.zone_name}|${w.name}`;

const getLabel = (w: SchedulingWindowReference): string => w.name;

// A key holds both parts of the reference, so an item can be reconstructed from it alone.
const asItem = (key: string): SchedulingWindowReference => {
  const [zone_name = '', name = ''] = key.split('|');
  return { zone_name, name };
};

/**
 * Searchable dropdown for scheduling windows keyed on the `zone_name|name` path. Selection returns the reference.
 *
 * Sibling of the id-keyed {@link EditSchedulingWindowIdReference}, whose props are kept in step with these: only
 * `value` / `onChange` differ, in type *and* in meaning.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch. Two-column row
 * layout (name + zone, muted right-aligned as informational context); the server matches on name only, which is the
 * natural UX when zones live in the background.
 *
 * A window with no zone (`scheduling_windows.zone_id` is nullable) has no `zone_name|name` path — the backend requires
 * both halves — so it cannot be referenced through this control and is not offered. Callers that store the numeric
 * id use the id-keyed sibling, which can.
 */
export const EditSchedulingWindowReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName }: Props) => {
  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<SchedulingWindowReference>>(
    () => ({
      kind: 'search',
      search: async (query, limit) => withPathsOnly(await searchSchedulingWindows({ name: query || undefined, limit, zone_name: zoneName })),
      // Required by the source contract, which cannot know whether a consumer ever holds a key-only
      // value — but neither runs here: the value carries its own label, so there is nothing to
      // hydrate. Built from the key anyway, since that is both the cheapest correct answer and the
      // only one available offline.
      resolve: async (key) => asItem(String(key)),
      interimValue: (key) => asItem(String(key)),
    }),
    [zoneName]
  );

  return (
    <EditReference<SchedulingWindowReference>
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(w, query) => (
        // <Highlighter> renders into a Fragment; wrap in a <span> so
        // the matched + unmatched pieces collapse into a single flex
        // item. Zone is informational only — plain text, no highlight.
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{w.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{w.zone_name}</span>
        </div>
      )}
    />
  );
};
